import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Alert, Card, CardBody, Container, Form, Input, Label, Button, Spinner } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import { API_HELPER } from "../../helpers/ApiHelper";
import { API_WEB_URLS } from "../../constants/constAPI";

const ForgetPasswordPage = () => {
  const navigate = useNavigate();
  document.title = "Forgot Password | Party Portal";

  // State Management
  const [step, setStep] = useState(1); // 1: Request OTP, 2: Enter OTP & New Password, 3: Success
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");

  // Countdown timer for resend OTP
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Mobile scroll & responsive lock handling
  useEffect(() => {
    document.body.classList.remove("no-overscroll");
    document.documentElement.classList.remove("no-overscroll");
    document.body.classList.add("allow-overscroll", "auth-active");
    document.documentElement.classList.add("allow-overscroll", "auth-active");

    document.body.style.setProperty("overflow-y", "auto", "important");
    document.documentElement.style.setProperty("overflow-y", "auto", "important");
    document.body.style.setProperty("height", "auto", "important");
    document.documentElement.style.setProperty("height", "auto", "important");
    document.body.style.setProperty("-webkit-overflow-scrolling", "touch", "important");
    document.documentElement.style.setProperty("-webkit-overflow-scrolling", "touch", "important");

    const rootEl = document.getElementById("root");
    if (rootEl) {
      rootEl.style.setProperty("height", "auto", "important");
      rootEl.style.setProperty("min-height", "100%", "important");
      rootEl.style.setProperty("overflow", "visible", "important");
    }

    return () => {
      document.body.classList.remove("allow-overscroll", "auth-active");
      document.documentElement.classList.remove("allow-overscroll", "auth-active");
      document.body.style.removeProperty("overflow-y");
      document.documentElement.style.removeProperty("overflow-y");
      document.body.style.removeProperty("height");
      document.documentElement.style.removeProperty("height");
      document.body.style.removeProperty("-webkit-overflow-scrolling");
      document.documentElement.style.removeProperty("-webkit-overflow-scrolling");
    };
  }, []);

  // Step 1: Send OTP
  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    const trimmedId = identifier.trim();
    if (!trimmedId) {
      setErrorMsg("Please enter your registered Username, Email address, or Mobile number.");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("Identifier", trimmedId);

      const res = await API_HELPER.apiPOST_Multipart(API_WEB_URLS.BASE + "ForgotPassword/SendOtp", formData);

      if (res && (res.success || res.status === 200)) {
        const masked = res.data?.maskedEmail || res.data?.MaskedEmail || "registered email";
        setMaskedEmail(masked);
        setSuccessMsg(res.message || "Verification OTP has been sent successfully.");
        setStep(2);
        setCountdown(60); // 60s cooldown for resend
      } else {
        const msg = res?.message || res?.data?.message || "Failed to send OTP. Please check your details and try again.";
        setErrorMsg(msg);
      }
    } catch (err) {
      setErrorMsg(err?.message || "A network or server error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Reset Password
  const handleResetPassword = async (e) => {
    if (e) e.preventDefault();
    setErrorMsg("");

    const trimmedOtp = otp.trim();
    if (!trimmedOtp || trimmedOtp.length !== 6) {
      setErrorMsg("Please enter the 6-digit OTP code sent to your email.");
      return;
    }

    if (!newPassword || newPassword.length < 4) {
      setErrorMsg("Password must be at least 4 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMsg("New Password and Confirm Password do not match.");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("Identifier", identifier.trim());
      formData.append("Otp", trimmedOtp);
      formData.append("NewPassword", newPassword);
      formData.append("ConfirmPassword", confirmPassword);

      const res = await API_HELPER.apiPOST_Multipart(API_WEB_URLS.BASE + "ForgotPassword/ResetPassword", formData);

      if (res && (res.success || res.status === 200)) {
        setStep(3);
      } else {
        const msg = res?.message || res?.data?.message || "Failed to reset password. Please check the OTP and try again.";
        setErrorMsg(msg);
      }
    } catch (err) {
      setErrorMsg(err?.message || "An error occurred while updating your password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <style>{`
        html.auth-active,
        body.auth-active {
          overflow-y: auto !important;
          overflow-x: hidden !important;
          height: auto !important;
          min-height: 100% !important;
          overscroll-behavior-y: auto !important;
          -webkit-overflow-scrolling: touch !important;
        }
        .forget-pwd-container {
          width: 100%;
          max-width: 520px;
          margin: 0 auto;
        }
        @media (max-width: 576px) {
          .forget-pwd-card-body {
            padding: 20px 16px !important;
          }
          .forget-pwd-title {
            font-size: 20px !important;
          }
        }
      `}</style>

      <div
        className="account-pages"
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          padding: "24px 14px",
          overflowY: "auto",
        }}
      >
        <Container className="forget-pwd-container">
          {/* Header Bar */}
          <div className="text-center mb-3">
            <h3 className="fw-bold text-white mb-1 forget-pwd-title">Reset Password</h3>
            <p className="text-white-50 font-size-13 mb-0">
              Recover your account access with verified email OTP
            </p>
          </div>

          <Card
            className="border-0 shadow-lg"
            style={{
              borderRadius: "16px",
              backgroundColor: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(10px)",
              overflow: "hidden",
            }}
          >
            <CardBody className="p-4 forget-pwd-card-body">
              {/* Error Alert */}
              {errorMsg ? (
                <Alert color="danger" className="border-0 shadow-sm mb-3 py-2 d-flex align-items-center" style={{ borderRadius: "10px" }}>
                  <i className="fas fa-exclamation-circle font-size-18 me-2 flex-shrink-0"></i>
                  <span className="font-size-13">{errorMsg}</span>
                </Alert>
              ) : null}

              {/* Success Info Alert */}
              {successMsg && step === 2 ? (
                <Alert color="success" className="border-0 shadow-sm mb-3 py-2 d-flex align-items-center" style={{ borderRadius: "10px" }}>
                  <i className="fas fa-check-circle font-size-18 me-2 flex-shrink-0"></i>
                  <span className="font-size-13">{successMsg}</span>
                </Alert>
              ) : null}

              {/* ================= STEP 1: REQUEST OTP ================= */}
              {step === 1 && (
                <Form onSubmit={handleSendOtp}>
                  <div className="text-center mb-3">
                    <div
                      className="d-inline-flex align-items-center justify-content-center rounded-circle mb-2"
                      style={{ width: "48px", height: "48px", background: "rgba(102, 126, 234, 0.12)", color: "#667eea" }}
                    >
                      <i className="fas fa-envelope-open-text font-size-20"></i>
                    </div>
                    <h5 className="fw-bold text-dark mb-1 font-size-16">Enter Account Identifier</h5>
                    <p className="text-muted font-size-13 mb-0">
                      Enter your registered Username, Email address, or Mobile number.
                    </p>
                  </div>

                  {/* Identifier Input */}
                  <div className="mb-3">
                    <Label className="form-label fw-semibold text-dark mb-1 font-size-13">
                      <i className="fas fa-user-tag me-2 text-primary"></i>
                      Username / Email / Mobile
                    </Label>
                    <div className="position-relative">
                      <Input
                        type="text"
                        className="form-control border-2"
                        placeholder="e.g. 9876543210 or user@example.com"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        required
                        disabled={loading}
                        style={{
                          borderRadius: "10px",
                          borderColor: "#e9ecef",
                          padding: "10px 16px 10px 40px",
                          fontSize: "14px",
                        }}
                      />
                      <i
                        className="fas fa-search position-absolute"
                        style={{
                          left: "14px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#667eea",
                          fontSize: "14px",
                        }}
                      ></i>
                    </div>
                  </div>

                  {/* Send OTP Button */}
                  <div className="d-grid mb-3">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="btn fw-semibold"
                      style={{
                        borderRadius: "10px",
                        padding: "11px",
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        border: "none",
                        color: "#fff",
                        fontSize: "14px",
                        boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Sending Verification OTP...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane me-2"></i>
                          Send Verification OTP
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Back to Login Link */}
                  <div className="text-center pt-2 border-top">
                    <Link to="/login" className="text-muted font-size-13 text-decoration-none fw-medium">
                      <i className="fas fa-arrow-left me-1"></i> Back to <strong>Login</strong>
                    </Link>
                  </div>
                </Form>
              )}

              {/* ================= STEP 2: ENTER OTP & NEW PASSWORD ================= */}
              {step === 2 && (
                <Form onSubmit={handleResetPassword}>
                  <div className="text-center mb-3">
                    <div
                      className="d-inline-flex align-items-center justify-content-center rounded-circle mb-2"
                      style={{ width: "48px", height: "48px", background: "rgba(16, 185, 129, 0.12)", color: "#10b981" }}
                    >
                      <i className="fas fa-shield-alt font-size-20"></i>
                    </div>
                    <h5 className="fw-bold text-dark mb-1 font-size-16">Verify OTP & Set Password</h5>
                    <p className="text-muted font-size-13 mb-0">
                      OTP has been sent to: <span className="badge bg-primary-subtle text-primary fw-bold font-size-12 px-2 py-1">{maskedEmail || "your email"}</span>
                    </p>
                  </div>

                  {/* OTP Field */}
                  <div className="mb-3">
                    <Label className="form-label fw-semibold text-dark mb-1 font-size-13">
                      <i className="fas fa-key me-2 text-primary"></i>
                      6-Digit OTP Code
                    </Label>
                    <div className="position-relative">
                      <Input
                        type="text"
                        maxLength="6"
                        className="form-control text-center fw-bold font-size-18 border-2"
                        placeholder="••••••"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                        required
                        disabled={loading}
                        style={{
                          borderRadius: "10px",
                          borderColor: "#667eea",
                          letterSpacing: "6px",
                          padding: "10px",
                        }}
                      />
                    </div>
                  </div>

                  {/* New Password */}
                  <div className="mb-3">
                    <Label className="form-label fw-semibold text-dark mb-1 font-size-13">
                      <i className="fas fa-lock me-2 text-primary"></i>
                      New Password
                    </Label>
                    <div className="position-relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        className="form-control border-2"
                        placeholder="Enter new password (min. 4 chars)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        disabled={loading}
                        style={{
                          borderRadius: "10px",
                          borderColor: "#e9ecef",
                          padding: "10px 42px 10px 40px",
                          fontSize: "14px",
                        }}
                      />
                      <i
                        className="fas fa-lock position-absolute"
                        style={{ left: "14px", top: "50%", transform: "translateY(-50%)", color: "#667eea" }}
                      ></i>
                      <button
                        type="button"
                        className="btn btn-link position-absolute p-0 text-muted"
                        style={{ right: "14px", top: "50%", transform: "translateY(-50%)", textDecoration: "none" }}
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <i className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"}></i>
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div className="mb-3">
                    <Label className="form-label fw-semibold text-dark mb-1 font-size-13">
                      <i className="fas fa-check-double me-2 text-primary"></i>
                      Confirm New Password
                    </Label>
                    <div className="position-relative">
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        className="form-control border-2"
                        placeholder="Re-enter new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={loading}
                        style={{
                          borderRadius: "10px",
                          borderColor: "#e9ecef",
                          padding: "10px 42px 10px 40px",
                          fontSize: "14px",
                        }}
                      />
                      <i
                        className="fas fa-check-double position-absolute"
                        style={{ left: "14px", top: "50%", transform: "translateY(-50%)", color: "#667eea" }}
                      ></i>
                      <button
                        type="button"
                        className="btn btn-link position-absolute p-0 text-muted"
                        style={{ right: "14px", top: "50%", transform: "translateY(-50%)", textDecoration: "none" }}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        <i className={showConfirmPassword ? "fas fa-eye-slash" : "fas fa-eye"}></i>
                      </button>
                    </div>
                  </div>

                  {/* Reset Password Button */}
                  <div className="d-grid mb-3">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="btn fw-semibold"
                      style={{
                        borderRadius: "10px",
                        padding: "11px",
                        background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        border: "none",
                        color: "#fff",
                        fontSize: "14px",
                        boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)",
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Resetting Password...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check-circle me-2"></i>
                          Confirm & Reset Password
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Resend OTP & Change Identifier Options */}
                  <div className="d-flex justify-content-between align-items-center pt-2 border-top font-size-13">
                    <button
                      type="button"
                      className="btn btn-link p-0 text-muted text-decoration-none font-size-12"
                      onClick={() => {
                        setStep(1);
                        setErrorMsg("");
                      }}
                    >
                      <i className="fas fa-edit me-1"></i> Change Details
                    </button>

                    <div>
                      {countdown > 0 ? (
                        <span className="text-muted font-size-12">
                          Resend in <strong className="text-primary">{countdown}s</strong>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-link p-0 text-primary text-decoration-none fw-semibold font-size-12"
                          onClick={() => handleSendOtp()}
                          disabled={loading}
                        >
                          <i className="fas fa-redo-alt me-1"></i> Resend OTP
                        </button>
                      )}
                    </div>
                  </div>
                </Form>
              )}

              {/* ================= STEP 3: SUCCESS CONFIRMATION ================= */}
              {step === 3 && (
                <div className="text-center py-3">
                  <div
                    className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3 shadow"
                    style={{
                      width: "68px",
                      height: "68px",
                      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      color: "#fff",
                    }}
                  >
                    <i className="fas fa-check font-size-32"></i>
                  </div>

                  <h4 className="fw-bold text-dark mb-2 font-size-18">Password Reset Successful!</h4>
                  <p className="text-muted font-size-13 mb-4">
                    Your password has been changed successfully. You can now sign in to the portal using your new password.
                  </p>

                  <div className="d-grid">
                    <Button
                      type="button"
                      onClick={() => navigate("/login")}
                      className="btn fw-semibold"
                      style={{
                        borderRadius: "10px",
                        padding: "11px",
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        border: "none",
                        color: "#fff",
                        fontSize: "14px",
                        boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                      }}
                    >
                      <i className="fas fa-sign-in-alt me-2"></i>
                      Proceed to Login
                    </Button>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Support Helpline */}
          <div className="mt-3 text-center">
            <small className="text-white-50 font-size-12">
              Need assistance? Support Helpline:{" "}
              <a href="tel:+919999999999" className="text-white fw-semibold text-decoration-underline">
                +91 99999-99999
              </a>
            </small>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

ForgetPasswordPage.propTypes = {
  history: PropTypes.object,
};

export default withRouter(ForgetPasswordPage);
